import { CustomProduct, Variant } from "@apptus/esales-api";
import type { Cart } from "@repo/core";

/**
 * Invoke this method when the cart has been emptied
 * @see https://developer.voyado.com/docs/tracking/web-activity-tracking#the-emptycart-method
 */
export function sendVoyadoEmptyCartEvent({
  cartRef,
}: {
  cartRef?: string | number;
}) {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.va === "undefined") {
    console.info(
      "sendVoyadoEmptyCartEvent - window.va is undefined skipping sendVoyadoEmptyCartEvent",
    );
    return;
  }

  if (!cartRef) {
    console.info(
      "sendVoyadoEmptyCartEvent - cartRef is undefined skipping sendVoyadoEmptyCartEvent",
    );
    return;
  }

  window.va("emptyCart", {
    cartRef: typeof cartRef === "number" ? cartRef.toString() : cartRef,
  });
}

/**
 * Invoke this method every time the cart is changed (items added/removed or the quantity updated)
 * @see https://developer.voyado.com/docs/tracking/web-activity-tracking#the-cart-method
 */
export function sendVoyadoCartChangeEvent({
  locale,
  cart,
}: {
  locale: string;
  cart?: Cart;
}) {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.va === "undefined") {
    console.info(
      "sendVoyadoCartChangeEvent - window.va is undefined skipping sendVoyadoCartChangeEvent",
    );
    return;
  }

  if (!cart) {
    console.info(
      "sendVoyadoCartChangeEvent - cart is undefined skipping sendVoyadoCartChangeEvent",
    );
    return;
  }

  const items = cart.items.map((cartItem) => ({
    itemId: cartItem.partNo,
    quantity: cartItem.quantity,
  }));

  window.va("cart", {
    cartRef: cart.id,
    locale,
    items,
  });
}

/**
 * As soon as a visitor is identified by the site the setContactId() method should be invoked with their unique contactId value
 * @see https://developer.voyado.com/docs/tracking/web-activity-tracking#using-setcontactid
 */
export function setVoyadoContactId(voyadoContactId?: string) {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.va === "undefined") {
    console.info(
      "setVoyadoContactId - window.va is undefined skipping setVoyadoContactId",
    );
    return;
  }

  if (!voyadoContactId) {
    console.info(
      "setVoyadoContactId - voyadoContactId is undefined skipping setVoyadoContactId",
    );
    return;
  }

  window.va("setContactId", voyadoContactId);
}

/**
 * Track product views
 * @see https://developer.voyado.com/docs/tracking/web-activity-tracking#tracking-product-views
 */
export function sendVoyadoProductViewEvent({
  locale,
  product,
  variant,
}: {
  locale: string;
  product: CustomProduct;
  variant: Variant;
}) {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.va === "undefined") {
    console.info(
      "sendVoyadoProductViewEvent - window.va is undefined skipping sendVoyadoProductViewEvent",
    );
    return;
  }

  if (!variant) {
    console.info(
      "sendVoyadoProductViewEvent - item is undefined skipping sendVoyadoProductViewEvent",
    );
    return;
  }

  window.va("productview", {
    categoryName: product.custom?.totalCategoryCodes?.join(" / "),
    itemId: variant.key,
    locale,
  });
}

export function sendVoyadoEmailRegistrationEvent({ email }: { email: string }) {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.va === "undefined") {
    console.info(
      "sendVoyadoEmailRegistrationEvent - window.va is undefined skipping sendVoyadoEmailRegistrationEvent",
    );
    return;
  }

  if (!email) {
    console.info(
      "sendVoyadoEmailRegistrationEvent - email is undefined skipping sendVoyadoEmailRegistrationEvent",
    );
    return;
  }

  window.va("emailRegistration", {
    email,
  });
}
